import {
  localCartLineId,
  type LocalCartDraft,
  type LocalCartLineInput,
} from "./cart-state";

/**
 * Replace one cart line with the exact set of user-confirmed configurations.
 *
 * Cart line identity/merge semantics stay owned by cart-state.ts through
 * localCartLineId(). This mutation preserves the current cart request/member/
 * claim/calculation context and invalidates the quote once, so editing multiple
 * configured items remains one local-first cart mutation.
 */
export function applyLocalCartLineConfigurations(
  cart: LocalCartDraft,
  sourceLineId: string,
  lines: LocalCartLineInput[],
) {
  if (lines.length === 0) return cart;

  const sourceIndex = cart.lines.findIndex((item) => item.lineId === sourceLineId);
  if (sourceIndex < 0) return cart;

  const source = cart.lines[sourceIndex];
  if (
    lines.length === source.quantity
    && lines.every((line) => localCartLineId(line) === sourceLineId)
  ) {
    return cart;
  }

  const nextLines = cart.lines.filter((item) => item.lineId !== sourceLineId);
  let insertionIndex = Math.min(sourceIndex, nextLines.length);

  for (const line of lines) {
    const lineId = localCartLineId(line);
    const existingIndex = nextLines.findIndex((item) => item.lineId === lineId);
    if (existingIndex >= 0) {
      const existing = nextLines[existingIndex];
      nextLines[existingIndex] = { ...existing, quantity: existing.quantity + 1 };
      continue;
    }

    nextLines.splice(insertionIndex, 0, { ...line, lineId, quantity: 1 });
    insertionIndex += 1;
  }

  return {
    ...cart,
    lines: nextLines,
    quote: null,
  };
}
